import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
  inject,
  signal,
  computed,
  ElementRef,
  Renderer2,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { filter } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { ApiSystemSettingsService } from '../../../core/services/api/menu/api-menu.service';
import { ApiSystemSettingsService as ApiSystemSettingsCoreService } from '../../../core/services/api/system-settings/api-system-settings.service';
import { MapperUserAccessMenuService } from '../../../core/mappers/mapper-user-menu.service';
import { MapperAccessUsersService } from '../../../core/mappers/system-settings/mapper-access-users.service';
import { MenuPermissionService } from '../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../core/services/site-state.service';
import { SiteOption, AccessUserDetailDto } from '../../../core/models/system-settings/access-users.model';
import {
  UserAccessMenu,
  UserAccessChild,
  UserAccessSubItem,
  FavoriteMenuItem,
  UserAccessResponseDto,
} from '../../../core/models/system-settings/user-menu.model';
import { RealtimeNotificationService } from '../../../core/services/realtime-notification.service';
import { SidebarStateService } from '../../../core/services/sidebar-state.service';
import { MenuNavigatorService, MenuLeaf } from '../../../core/services/menu-navigator.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
  host: {
    '[class.sidebar-collapsed]': 'isCollapsed()',
  },
})
export class SidebarComponent implements OnInit, OnDestroy {
  /** desktop = แถบซ้ายถาวร ย่อ/ขยายได้ · drawer = เมนูสไลด์บนแท็บเล็ต/มือถือ (ขยายเสมอ) */
  @Input() variant: 'desktop' | 'drawer' = 'desktop';
  @Output() navigated = new EventEmitter<void>();
  @Output() closeRequested = new EventEmitter<void>();

  readonly nav = inject(MenuNavigatorService);
  menuGroups: UserAccessMenu[] = [];
  showDragHint = false;
  isDraggingOverUrl: string | null = null;
  favoriteIds = this.nav.favoriteIds;
  favoriteList = signal<FavoriteMenuItem[]>([]);
  isMenuLoading = signal<boolean>(true);
  appVersion = '';
  
  private sidebarStateService = inject(SidebarStateService);
  isSidebarLocked = this.sidebarStateService.isSidebarLocked;
  readonly searchQuery = this.sidebarStateService.searchQuery;
  readonly isSearching = computed(() => !!this.searchQuery().trim());
  readonly searchResults = computed<MenuLeaf[]>(() => this.nav.search(this.searchQuery()));
  readonly isCollapsed = computed(() => this.variant === 'desktop' && !this.isSidebarLocked());
  
  showSiteDropdown = false;
  siteSearchText = '';

  /** Body-level magnifier tooltip (outside sidebar overflow/transform) */
  private tooltipEl: HTMLElement | null = null;
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);

  // For auto-expanding collapsed menus on drag hover
  dragCounter = new Map<any, number>();
  temporarilyExpandedItems = new Set<any>();
  dragExpandTimers = new Map<any, any>();

  private cookieService = inject(CookieService);
  private apiSystemSettingsService = inject(ApiSystemSettingsService);
  private apiSystemSettingsCoreService = inject(ApiSystemSettingsCoreService);
  private mapper = inject(MapperUserAccessMenuService);
  private mapperAccessUsers = inject(MapperAccessUsersService);
  private permissionService = inject(MenuPermissionService);
  readonly siteState = inject(SiteStateService);
  private readonly http = inject(HttpClient);

  // --- Realtime notification counts (used in template via async pipe) ---
  get parkingExitCount$() {
    return this.notifService.parkingExitCount$;
  }
  get evExitCount$() {
    return this.notifService.evExitCount$;
  }

  /** LIVE menu paths — must stay in sync with mapper-user-menu.service.ts */
  readonly ROUTE_PARKING_TRANSACTION = '/parking/transaction-parking';
  readonly ROUTE_EV_CHARGER_LIST = '/cityparking/ev-charger-list';

  // ================= Normal Functions =================

  showNavTooltip(event: MouseEvent, text: string): void {
    if (!isPlatformBrowser(this.platformId) || !text?.trim()) {
      return;
    }

    const el = event.currentTarget as HTMLElement | null;
    if (!el) {
      return;
    }

    // Only show when the visible label is truncated (...).
    if (el.scrollWidth <= el.clientWidth + 1) {
      return;
    }

    const labelRect = el.getBoundingClientRect();
    // Anchor to the sidebar host right edge so the tip sits outside the layout.
    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const left = Math.round(hostRect.right + 10);
    const top = Math.round(labelRect.top + labelRect.height / 2);

    this.ensureTooltipEl();
    if (!this.tooltipEl) {
      return;
    }

    const textEl = this.tooltipEl.querySelector('.nav-magnifier-text');
    if (textEl) {
      textEl.textContent = text.trim();
    }

    this.renderer.setStyle(this.tooltipEl, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipEl, 'left', `${left}px`);
    this.renderer.addClass(this.tooltipEl, 'is-visible');
  }

  hideNavTooltip(): void {
    if (!this.tooltipEl) {
      return;
    }
    this.renderer.removeClass(this.tooltipEl, 'is-visible');
  }

  private ensureTooltipEl(): void {
    if (this.tooltipEl || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const tip = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(tip, 'nav-magnifier-tooltip');
    this.renderer.setAttribute(tip, 'role', 'tooltip');

    const icon = this.renderer.createElement('span') as HTMLElement;
    this.renderer.addClass(icon, 'material-icons-outlined');
    this.renderer.addClass(icon, 'nav-magnifier-icon');
    icon.textContent = 'zoom_in';

    const text = this.renderer.createElement('span') as HTMLElement;
    this.renderer.addClass(text, 'nav-magnifier-text');

    this.renderer.appendChild(tip, icon);
    this.renderer.appendChild(tip, text);
    this.renderer.appendChild(this.document.body, tip);
    this.tooltipEl = tip;
  }

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private notifService: RealtimeNotificationService,
  ) { }

  ngOnInit(): void {
    this.loadAppVersion();

    // Auto Expand logic: เปิดเมนูค้างไว้ถ้า Route นั้น Active อยู่
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.checkActiveMenu();
        if (this.variant === 'desktop') {
          this.nav.trackVisit(this.router.url);
        }
      });

    // Load user favorites from localStorage
    this.loadFavorites();

    // Check ครั้งแรกตอนโหลด
    this.loadUserMenu();
    this.loadUserSites();

    // Show drag hint if not dismissed before
    if (isPlatformBrowser(this.platformId)) {
      const dismissed = localStorage.getItem('sidebar_drag_hint_dismissed');
      this.showDragHint = !dismissed;
    }
  }

  ngOnDestroy(): void {
    this.hideNavTooltip();
    if (this.tooltipEl?.parentNode) {
      this.renderer.removeChild(this.document.body, this.tooltipEl);
    }
    this.tooltipEl = null;
  }

  private loadAppVersion(): void {
    this.http
      .get<{ version: string }>('./json/version.json', {
        headers: { 'Cache-Control': 'no-cache' },
      })
      .subscribe({
        next: (response) => {
          this.appVersion = (response?.version || '').trim();
        },
        error: () => {
          this.appVersion = '';
        },
      });
  }

  /** Filters site options based on search text typed in the dropdown */
  get filteredSites(): SiteOption[] {
    const q = this.siteSearchText.trim().toLowerCase();
    if (!q) return this.siteState.sites();
    return this.siteState.sites().filter((s) => s.name.toLowerCase().includes(q));
  }

  /** Toggles the site selector dropdown visibility */
  toggleSiteDropdown(): void {
    this.showSiteDropdown = !this.showSiteDropdown;
    if (this.showSiteDropdown) {
      this.siteSearchText = '';
    }
  }

  /** Closes the site dropdown (called on blur/outside click) */
  closeSiteDropdown(): void {
    this.showSiteDropdown = false;
  }

  /** Called when a user selects a site from the dropdown */
  onSiteSelect(site: SiteOption): void {
    this.siteState.setSelectedSite(site);
    this.showSiteDropdown = false;
    this.siteSearchText = '';
  }

  /** Prevents premature blur when clicking dropdown elements */
  preventBlur(event: MouseEvent): void {
    event.preventDefault();
  }

  dismissDragHint(): void {
    this.showDragHint = false;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('sidebar_drag_hint_dismissed', 'true');
    }
  }

  /**
   * Toggles the lock/pinned state of the sidebar on desktop view.
   * Persists preferences to localStorage.
   */
  toggleSidebarLock(): void {
    this.sidebarStateService.toggleSidebarLock();
  }

  onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.searchQuery.set('');
  }

  openPalette(): void {
    this.nav.openPalette();
  }

  /** เปิดเฉพาะเมนูแม่ที่มีหน้าปัจจุบันอยู่ — เมนูอื่นปิด (accordion) */
  checkActiveMenu() {
    const currentUrl = this.router.url;
    const active = this.findActiveParent(currentUrl);
    if (!active) return;
    this.allParents().forEach((item) => (item.expanded = item === active));
  }

  toggleSubmenu(item: UserAccessChild) {
    if (!item.submenu) return;
    const willOpen = !item.expanded;
    this.allParents().forEach((p) => (p.expanded = false));
    item.expanded = willOpen;
  }

  /** Clear notification badge when user navigates to a live page. */
  onMenuNavigate(url: string | undefined): void {
    if (url === this.ROUTE_PARKING_TRANSACTION) {
      this.clearParkingNotif();
    } else if (url === this.ROUTE_EV_CHARGER_LIST) {
      this.clearEvNotif();
    }
    this.navigated.emit();
  }

  /** ตอนย่อเมนู: กดไอคอนแล้วขยายแถบ และเปิดเมนูย่อยนั้นทันที */
  expandToItem(item: UserAccessChild): void {
    if (this.isCollapsed()) {
      this.sidebarStateService.toggleSidebarLock();
    }
    this.allParents().forEach((p) => (p.expanded = p === item));
  }

  isParentActive(item: UserAccessChild): boolean {
    return this.findActiveParent(this.router.url) === item;
  }

  /** ไฮไลต์คำค้นในผลค้นหา โดยไม่ใช้ innerHTML */
  highlight(text: string): { text: string; hit: boolean }[] {
    const terms = this.searchQuery().toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!terms.length || !text) return [{ text, hit: false }];
    const lower = text.toLowerCase();
    const marks = new Array<boolean>(text.length).fill(false);
    for (const t of terms) {
      let from = 0;
      let at: number;
      while ((at = lower.indexOf(t, from)) >= 0) {
        marks.fill(true, at, at + t.length);
        from = at + t.length;
      }
    }
    const parts: { text: string; hit: boolean }[] = [];
    for (let i = 0; i < text.length; i++) {
      const last = parts[parts.length - 1];
      if (last && last.hit === marks[i]) last.text += text[i];
      else parts.push({ text: text[i], hit: marks[i] });
    }
    return parts;
  }

  private allParents(): UserAccessChild[] {
    return this.menuGroups.flatMap((g) => g.children.filter((c) => !!c.submenu?.length));
  }

  private findActiveParent(currentUrl: string): UserAccessChild | undefined {
    const path = currentUrl.split('?')[0];
    return this.allParents().find((item) =>
      item.submenu!.some((sub) => !!sub.url && (path === sub.url || path.startsWith(sub.url + '/'))),
    );
  }

  isParkingLiveRoute(url: string | undefined): boolean {
    return url === this.ROUTE_PARKING_TRANSACTION;
  }

  isEvChargerLiveRoute(url: string | undefined): boolean {
    return url === this.ROUTE_EV_CHARGER_LIST;
  }

  clearParkingNotif(): void {
    this.notifService.clearParking();
  }
  clearEvNotif(): void {
    this.notifService.clearEv();
  }

  // --- Drag & Drop from Modal ---
  onDragOver(event: DragEvent, subItem: any) {
    if (subItem.url === '/cityparking/line-oa-member') {
      // Only allow dropping here for this feature
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
      this.isDraggingOverUrl = subItem.url;
    }
  }

  onDragLeave(event: DragEvent, subItem: any) {
    this.isDraggingOverUrl = null;
  }

  onDrop(event: DragEvent, subItem: any) {
    if (subItem.url === '/cityparking/line-oa-member') {
      event.preventDefault();
      this.isDraggingOverUrl = null;
      const data = event.dataTransfer?.getData('application/json');
      if (data) {
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'LINE_MEMBER_SEARCH') {
            sessionStorage.setItem('pendingLineOaSearch', data);
            this.router.navigate([subItem.url]);
          }
        } catch (e) { }
      }
    }
  }

  // --- Auto-expand parent menu on drag hover (Standardized) ---
  onListDragEnter(event: DragEvent, item: any) {
    if (!item.submenu) return;

    let count = this.dragCounter.get(item) || 0;
    this.dragCounter.set(item, count + 1);

    if (count === 0 && !item.expanded) {
      // First enter and it's currently collapsed
      const timer = setTimeout(() => {
        item.expanded = true;
        this.temporarilyExpandedItems.add(item);
        this.dragExpandTimers.delete(item);
      }, 500); // 500ms delay for snappier standard feel
      this.dragExpandTimers.set(item, timer);
    }
  }

  onListDragOver(event: DragEvent, item: any) {
    if (item.submenu) {
      event.preventDefault(); // Necessary to allow dropping and hover detection
    }
  }

  onListDragLeave(event: DragEvent, item: any) {
    if (!item.submenu) return;

    let count = this.dragCounter.get(item) || 0;
    count = Math.max(0, count - 1);
    this.dragCounter.set(item, count);

    if (count === 0) {
      // Mouse left the li entirely
      const timer = this.dragExpandTimers.get(item);
      if (timer) {
        clearTimeout(timer);
        this.dragExpandTimers.delete(item);
      }

      if (this.temporarilyExpandedItems.has(item)) {
        item.expanded = false;
        this.temporarilyExpandedItems.delete(item);
      }
    }
  }

  /**
   * Loads the list of favorite menu IDs from local storage.
   */
  loadFavorites(): void {
    let ids: number[] = [];
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem('sidebar_favorite_menu_ids');
      if (stored) {
        try {
          ids = JSON.parse(stored);
        } catch (e) {
          ids = [];
        }
      }
    }
    this.favoriteIds.set(ids);
    this.buildFavoriteList();
  }

  /**
   * Builds the favoriteList array by scanning all loaded menu groups and matching the favorited IDs.
   */
  buildFavoriteList(): void {
    const list: FavoriteMenuItem[] = [];
    if (!this.menuGroups) return;

    this.menuGroups.forEach((group) => {
      if (group.children) {
        group.children.forEach((child) => {
          if (child.submenu) {
            child.submenu.forEach((sub) => {
              if (this.favoriteIds().includes(sub.id)) {
                list.push({
                  id: sub.id,
                  name: sub.name,
                  url: sub.url || sub.path || '',
                  icon: child.icon || 'star',
                  live: sub.live,
                  badge: sub.badge,
                });
              }
            });
          } else {
            if (this.favoriteIds().includes(child.id)) {
              list.push({
                id: child.id,
                name: child.name,
                url: child.url || child.path || '',
                icon: child.icon || 'star',
                live: child.live,
                badge: child.badge,
              });
            }
          }
        });
      }
    });
    this.favoriteList.set(list);
  }

  /**
   * Toggles the favorite status of a menu item.
   */
  toggleFavorite(
    item: UserAccessChild | UserAccessSubItem | FavoriteMenuItem,
    event: Event,
  ): void {
    event.stopPropagation();
    event.preventDefault();
    const id = item.id;
    const currentIds = this.favoriteIds();
    const index = currentIds.indexOf(id);
    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;

    if (index >= 0) {
      this.apiSystemSettingsService
        .deleteFavoriteMenu(id, userId, id)
        .subscribe({
          next: () => {
            const newIds = [...currentIds];
            newIds.splice(index, 1);
            this.favoriteIds.set(newIds);
            if (isPlatformBrowser(this.platformId)) {
              localStorage.setItem(
                'sidebar_favorite_menu_ids',
                JSON.stringify(newIds),
              );
            }
            this.buildFavoriteList();
          },
          error: (err) =>
            console.error('[Sidebar] Failed to delete favorite menu', err),
        });
    } else {
      this.apiSystemSettingsService
        .insertFavoriteMenu({ menuId: id, userId: userId })
        .subscribe({
          next: () => {
            const newIds = [...currentIds];
            newIds.push(id);
            this.favoriteIds.set(newIds);
            if (isPlatformBrowser(this.platformId)) {
              localStorage.setItem(
                'sidebar_favorite_menu_ids',
                JSON.stringify(newIds),
              );
            }
            this.buildFavoriteList();
          },
          error: (err) =>
            console.error('[Sidebar] Failed to insert favorite menu', err),
        });
    }
  }

  isFavorite(id: number): boolean {
    return this.favoriteIds().includes(id);
  }

  // ================= API Functions =================

  private loadUserSites(): void {
    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;

    if (userId > 0) {
      this.apiSystemSettingsCoreService.getAccessUserById(userId).subscribe({
        next: (dto: AccessUserDetailDto) => {
          const sites = this.mapperAccessUsers.mapSitesFromDto(dto);
          this.siteState.setSites(sites);
        },
        error: (err: unknown) => {
          console.error('[Sidebar] Failed to load user sites:', err);
        },
      });
    }
  }

  loadUserMenu(): void {
    this.isMenuLoading.set(true);
    this.permissionService.loadUserMenus().subscribe({
      next: (data: UserAccessResponseDto | null) => {
        console.log("Menu Load user : ", data);

        this.menuGroups = this.permissionService.menuGroups();
        this.checkActiveMenu();
        if (this.variant === 'desktop') {
          this.nav.trackVisit(this.router.url);
        }

        if (data?.listFavoriteMenu && data.listFavoriteMenu.length > 0) {
          const favorites = this.mapper.mapFavoriteFromDto(data.listFavoriteMenu);
          this.favoriteList.set(favorites);
          this.favoriteIds.set(favorites.map((f) => f.id));

          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(
              'sidebar_favorite_menu_ids',
              JSON.stringify(favorites.map((f) => f.id)),
            );
          }
        } else {
          this.buildFavoriteList();
        }
        this.isMenuLoading.set(false);
      },
      error: (err) => {
        console.error('[Sidebar] Failed to load dynamic user access menu:', err);
        this.menuGroups = [];
        this.checkActiveMenu();
        this.buildFavoriteList();
        this.isMenuLoading.set(false);
      },
    });
  }
}
